import dayjs from "dayjs"
import { getUserAvatarUrl, resolveMediaUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"

export const NA = "N/A"

export const CALL_DETAIL_WITH = [
  "user",
  "actionBy",
  "projects",
  "categories",
  "cities",
  "media",
]

export const CALL_LIST_WITH = [
  "cities",
  "user",
  "actionBy",
  "projects",
  "categories",
  "media",
]

export const CITY_CALL_RESPONSE_WITH = [
  "parent",
  "parent.user.currentRoles.roleTypes",
  "calls",
  "user.currentRoles.roleTypes",
  "replies.user.currentRoles.roleTypes",
]

export const DEFAULT_CALL_RESPONSE_SORT_BY = "created_at"
export const DEFAULT_CALL_RESPONSE_SORT_DIRECTION = "desc"
export const CALL_RESPONSE_PAGE_SIZE = 10

export const isCallResponseReverseInfinite = (
  sortBy = DEFAULT_CALL_RESPONSE_SORT_BY,
  sortDirection = DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
) =>
  sortBy === DEFAULT_CALL_RESPONSE_SORT_BY && sortDirection === "desc"

export const CALL_RESPONSE_SORT_OPTIONS = [
  { value: "created_at", label: "Most Recent" },
  { value: "likes_count", label: "Most Likes" },
]

export const compareCallResponseThreads = (
  a,
  b,
  sortBy = DEFAULT_CALL_RESPONSE_SORT_BY,
  sortDirection = DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
) => {
  const dir = sortDirection === "asc" ? 1 : -1

  if (sortBy === "likes_count") {
    return ((Number(a?.likes) || 0) - (Number(b?.likes) || 0)) * dir
  }

  const aTime = dayjs(a?.raw?.created_at).valueOf()
  const bTime = dayjs(b?.raw?.created_at).valueOf()
  const safeA = Number.isFinite(aTime) ? aTime : 0
  const safeB = Number.isFinite(bTime) ? bTime : 0
  return (safeA - safeB) * dir
}

export const extractCallResponsePage = payload => {
  const result = payload?.data?.result
  if (Array.isArray(result)) {
    return {
      list: result,
      currentPage: 1,
      lastPage: 1,
      total: result.length,
    }
  }

  const list = Array.isArray(result?.data) ? result.data : []
  const perPage = Number(result?.per_page || CALL_RESPONSE_PAGE_SIZE)
  const total = Number(result?.total ?? list.length)
  const currentPage = Number(result?.current_page || 1)
  const lastPage = Math.max(
    1,
    Number(result?.last_page || Math.ceil((total || 0) / (perPage || 1)) || 1),
  )

  return { list, currentPage, lastPage, total }
}

export const mergeCallResponseItems = (prev = [], next = []) => {
  const seen = new Set(prev.map(item => String(item?.id || "")))
  const extra = next.filter(item => item?.id && !seen.has(String(item.id)))
  return extra.length ? [...prev, ...extra] : prev
}

export const patchCallResponseLike = (items, id, { liked, likes } = {}) =>
  (items || []).map(item => {
    if (String(item?.id) !== String(id)) return item
    return {
      ...item,
      ...(likes != null ? { likes_count: likes } : {}),
      ...(liked != null ? { has_current_user_liked: liked } : {}),
    }
  })

export const upsertCallResponseItem = (
  items = [],
  incoming,
  { replace = false } = {},
) => {
  if (!incoming?.id && incoming?.id !== 0) return items
  const id = String(incoming.id)
  const index = items.findIndex(item => String(item?.id) === id)
  if (index === -1) return [incoming, ...items]
  if (!replace) return items
  const existing = items[index]
  const next = [...items]
  next[index] = {
    ...existing,
    ...incoming,
    user: incoming.user || existing.user,
    has_current_user_liked:
      incoming.has_current_user_liked ?? existing.has_current_user_liked,
    likes_count:
      incoming.likes_count != null
        ? incoming.likes_count
        : existing.likes_count,
  }
  return next
}

const nestedResponseLists = item =>
  [item?.replies, item?.comments, item?.children].filter(Array.isArray)

const responseParentId = item =>
  item?.parent_comment_id || item?.parent_id || item?.parent?.id || null

/** Flatten nested reply relations so threads + delete share one list. */
const collectCallResponseItems = (items, acc = [], seen = new Set()) => {
  ;(items || []).forEach(item => {
    if (!item || item.id == null) return
    const key = String(item.id)
    if (!seen.has(key)) {
      seen.add(key)
      acc.push(item)
    }
    nestedResponseLists(item).forEach(list => {
      list.forEach(child => {
        if (!child || child.id == null) return
        const withParent =
          child.parent_comment_id || child.parent_id
            ? child
            : { ...child, parent_comment_id: item.id }
        collectCallResponseItems([withParent], acc, seen)
      })
    })
  })
  return acc
}

export const removeCallResponseById = (items, id) => {
  const target = String(id)
  if (!target || target === "undefined" || target === "null") {
    return items || []
  }

  const idsToRemove = new Set([target])

  const walk = list => {
    ;(list || []).forEach(item => {
      const itemId = item?.id != null ? String(item.id) : ""
      const parentId = responseParentId(item)
      if (itemId && idsToRemove.has(itemId)) {
        nestedResponseLists(item).forEach(nested => {
          nested.forEach(child => {
            if (child?.id != null) idsToRemove.add(String(child.id))
          })
        })
      } else if (
        itemId &&
        parentId &&
        idsToRemove.has(String(parentId))
      ) {
        idsToRemove.add(itemId)
      }
      nestedResponseLists(item).forEach(walk)
    })
  }

  let previousSize = 0
  while (idsToRemove.size !== previousSize) {
    previousSize = idsToRemove.size
    walk(items)
  }

  const prune = list =>
    (list || [])
      .filter(item => item?.id == null || !idsToRemove.has(String(item.id)))
      .map(item => {
        const next = { ...item }
        if (next.parent && idsToRemove.has(String(next.parent.id))) {
          next.parent = null
        }
        if (
          next.parent_comment_id &&
          idsToRemove.has(String(next.parent_comment_id))
        ) {
          next.parent_comment_id = null
        }
        if (Array.isArray(next.replies)) next.replies = prune(next.replies)
        if (Array.isArray(next.comments)) next.comments = prune(next.comments)
        if (Array.isArray(next.children)) next.children = prune(next.children)
        const nestedCount = [
          next.replies,
          next.comments,
          next.children,
        ].reduce(
          (sum, nested) =>
            sum + (Array.isArray(nested) ? nested.length : 0),
          0,
        )
        if (nestedCount > 0 || next.replies_count != null) {
          next.replies_count = nestedCount
        }
        return next
      })

  return prune(items)
}

export const countCallResponseItems = items =>
  collectCallResponseItems(items).length

export const removeCallResponseTree = (items, id) => {
  const before = countCallResponseItems(items)
  const next = removeCallResponseById(items, id)
  const after = countCallResponseItems(next)
  return {
    next,
    removedCount: Math.max(1, before - after),
  }
}

export const displayOrNa = value => {
  if (value == null) return NA
  const text = String(value).trim()
  return text || NA
}

export const formatPersonName = person => {
  if (!person) return NA
  return getUserFullName(person, { fallback: NA })
}

export const formatCity = cities => {
  if (!Array.isArray(cities) || cities.length === 0) return NA

  const primary =
    cities.find(c => c?.pivot?.address_type === "primary") || cities[0]
  if (!primary) return NA

  const parts = [primary.city, primary.state, primary.country]
    .map(part => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)

  return parts.length > 0 ? parts.join(", ") : NA
}

const extractCategoryName = cat => {
  if (!cat) return ""
  if (typeof cat === "string") return cat.trim()

  const nested = cat.category && typeof cat.category === "object" ? cat.category : null
  const raw =
    cat.name ||
    cat.title ||
    cat.label ||
    cat.public_name ||
    cat.category_name ||
    nested?.name ||
    nested?.title ||
    nested?.label ||
    ""

  if (typeof raw === "string") return raw.trim()
  if (raw && typeof raw === "object") {
    return String(raw.en || raw.en_US || Object.values(raw)[0] || "").trim()
  }
  return ""
}

export const getCallCategoryNames = source => {
  const call = source?.raw && typeof source.raw === "object" ? source.raw : source || {}
  const raw =
    call.categories ||
    call.category ||
    source?.categories ||
    source?.category ||
    []
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : raw
        ? [raw]
        : []
  return [...new Set(list.map(extractCategoryName).filter(Boolean))]
}

export const getVisibleCallCategories = source => {
  if (Array.isArray(source?.categoryNames) && source.categoryNames.length) {
    return source.categoryNames.filter(name => name && name !== NA)
  }
  return getCallCategoryNames(source).filter(name => name && name !== NA)
}

export const formatCategories = categories => {
  const names = getCallCategoryNames({ categories })
  return names.length > 0 ? names.join(", ") : NA
}

export const formatDeadline = expiresAt => {
  if (!expiresAt) return null
  const end = dayjs(expiresAt)
  if (!end.isValid()) return null

  const days = end.startOf("day").diff(dayjs().startOf("day"), "day")
  if (days < 0) {
    const ago = Math.abs(days)
    return `Ended ${ago} day${ago === 1 ? "" : "s"} ago`
  }
  if (days === 0) return "Ends today"
  return `${days} day${days === 1 ? "" : "s"} left`
}

export const formatPosted = publishedAt => {
  if (!publishedAt) return NA
  const date = dayjs(publishedAt)
  if (!date.isValid()) return NA
  return date.format("MMM D, YYYY")
}

export const resolveCallImage = call => {
  const mediaList = Array.isArray(call?.media) ? call.media : []
  const firstMedia = mediaList[0]

  // Prefer absolute CDN/S3 URLs from the API — relative `path` keys are not
  // browsable via the API origin and produce broken images.
  // Do not fall back to city cover art; UI shows thumbnailPlaceholder instead.
  return (
    resolveMediaUrl(firstMedia?.thumbnail_path_url) ||
    resolveMediaUrl(firstMedia?.path_url) ||
    resolveMediaUrl(firstMedia?.url) ||
    resolveMediaUrl(firstMedia?.image) ||
    resolveMediaUrl(firstMedia?.thumbnail) ||
    null
  )
}

export const callTypeLabel = type => {
  const value = String(type || "")
    .trim()
    .toLowerCase()
  if (value === "formal") return "Formal Call for Artists"
  if (value === "informal") return "Informal Inbox"
  return displayOrNa(type)
}

export const getCallTypeBadgeLabel = type => {
  const value = String(type || "")
    .trim()
    .toLowerCase()
  if (value === "formal") return "Formal"
  if (value === "informal") return "Informal"
  return null
}

export const getCallTypeBadgeClass = type => {
  const value = String(type || "")
    .trim()
    .toLowerCase()
  if (value === "formal") return "calls-for-artist__badge--formal"
  if (value === "informal") return "calls-for-artist__badge--informal"
  return ""
}

export const statusTone = status => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "review"
    case "rejected":
      return "draft"
    case "closed":
      return "closed"
    case "published":
    default:
      return "published"
  }
}

export const isCallActive = call => {
  if (String(call?.status || "").toLowerCase() !== "published") return false
  if (!call?.expires_at) return true
  const end = dayjs(call.expires_at)
  return end.isValid() ? end.isAfter(dayjs()) : true
}

export const getCallStatusLabel = call => {
  if (call?.active) return "Active"
  switch (call?.status) {
    case "pending":
      return "Under Review"
    case "rejected":
      return "Rejected"
    case "closed":
      return "Closed"
    case "published":
      return "Published"
    default:
      return displayOrNa(call?.status)
  }
}

export const getListStatusBadgeClass = call => {
  if (call?.active) return "calls-for-artist__badge--active"
  switch (call?.status) {
    case "pending":
      return "calls-for-artist__badge--review"
    case "rejected":
      return "calls-for-artist__badge--draft"
    case "closed":
      return "calls-for-artist__badge--closed"
    case "published":
      return "calls-for-artist__badge--active"
    default:
      return "calls-for-artist__badge--closed"
  }
}

export const getDetailStatusBadgeClass = call => {
  if (call?.active) return "calls-for-artist-detail__badge--active"
  switch (call?.status) {
    case "pending":
      return "calls-for-artist-detail__badge--review"
    case "rejected":
      return "calls-for-artist-detail__badge--rejected"
    case "closed":
      return "calls-for-artist-detail__badge--closed"
    case "published":
      return "calls-for-artist-detail__badge--published"
    default:
      return "calls-for-artist-detail__badge--closed"
  }
}

export const mapApiCall = call => {
  const project = Array.isArray(call?.projects) ? call.projects[0] : null
  const user = call?.user || null
  const responses = Number(call?.total_responses ?? 0)
  const callTitle = String(call?.title || "").trim()

  return {
    id: call?.id,
    title: displayOrNa(project?.name),
    headline: callTitle || NA,
    description: displayOrNa(call?.details),
    city: call?.is_global ? "Global" : formatCity(call?.cities),
    categoryNames: getCallCategoryNames(call),
    category: formatCategories(call?.categories || call?.category),
    posted: formatPosted(call?.published_at || call?.created_at),
    deadline: formatDeadline(call?.expires_at),
    status: String(call?.status || "")
      .trim()
      .toLowerCase(),
    active: isCallActive(call),
    responses: Number.isFinite(responses) ? Math.max(0, responses) : 0,
    responsePeople: user
      ? [
          {
            name: formatPersonName(user),
            src: getUserAvatarUrl(user),
          },
        ]
      : [],
    responseAvatars: [],
    image: resolveCallImage(call),
    projectCode: project?.name
      ? project.name
          .split(/\s+/)
          .map(word => word[0])
          .join("")
          .slice(0, 2)
          .toUpperCase()
      : NA,
    projectName: displayOrNa(project?.name),
    project: project?.name
      ? { name: project.name, id: project.id || null }
      : null,
    postedBy: {
      id: user?.id || null,
      name: formatPersonName(user),
      avatar: getUserAvatarUrl(user),
    },
    poster: {
      id: user?.id || null,
      name: formatPersonName(user),
      avatar: getUserAvatarUrl(user),
    },
    type: call?.type || null,
    isGlobal: Boolean(call?.is_global),
    raw: call,
  }
}

/** City id for city-scoped call APIs (reply / delete). Prefer primary. */
export const getCallCityId = call => {
  const source = call?.raw || call || {}
  const cities = Array.isArray(source.cities) ? source.cities : []
  const primary =
    cities.find(
      city =>
        String(city?.pivot?.address_type || "").toLowerCase() === "primary",
    ) || cities[0]

  return primary?.id || primary?.city_id || source.city_id || null
}

/** City Hub opportunity card — keep every category for tags and filters. */
export const mapApiCallToCityHubCard = call => mapApiCall(call)

export const formatRelativeTime = value => {
  if (!value) return NA
  const date = dayjs(value)
  if (!date.isValid()) return NA

  const now = dayjs()
  const diffMinutes = now.diff(date, "minute")
  if (diffMinutes < 1) return "Just now"
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`
  }

  const diffHours = now.diff(date, "hour")
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`
  }

  const diffDays = now.diff(date, "day")
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`
  }

  return date.format("MMM D, YYYY")
}

const getUserRoleBadge = user => {
  const roles = user?.current_roles || user?.currentRoles || []
  const ongoing = roles.find(role => role?.is_ongoing)
  const pick = ongoing || roles[0]
  const types = pick?.role_types || pick?.roleTypes || []
  const type = types[0]
  return (
    type?.public_name ||
    type?.name ||
    (typeof pick?.role === "string" ? pick.role : "") ||
    user?.ongoing_role?.name ||
    user?.role_type?.public_name ||
    user?.role_type?.name ||
    user?.role ||
    ""
  )
}

const mapResponseUser = (item, usersById = {}) => {
  const user = item?.user || usersById[String(item?.user_id || "")] || null

  return {
    name: formatPersonName(user),
    avatar: getUserAvatarUrl(user),
    badge: displayOrNa(getUserRoleBadge(user) || item?.badge),
  }
}

/** Call ids this comment is morph-attached to (`calls[]` / pivot). */
export const getCommentableCallIds = item => {
  const ids = new Set()
  const calls = Array.isArray(item?.calls) ? item.calls : []

  calls.forEach(call => {
    const id = call?.id || call?.pivot?.commentable_id
    if (id) ids.add(String(id))
  })

  if (item?.call_id) ids.add(String(item.call_id))
  if (item?.pivot?.commentable_id) ids.add(String(item.pivot.commentable_id))

  return [...ids]
}

export const isCallResponseForCall = (item, callId) => {
  if (!callId) return true
  const ids = getCommentableCallIds(item)
  // No morph data — trust the list endpoint (already scoped by call id).
  if (!ids.length) return true
  return ids.includes(String(callId))
}

export const mapCallResponseItem = (item, options = {}) => {
  const { usersById = {}, authorUserId = null } = options
  const person = mapResponseUser(item, usersById)
  const likes = Number(item?.likes_count ?? 0)
  const replies = Number(item?.replies_count ?? 0)

  return {
    id: item?.id,
    parentId: item?.parent_comment_id || null,
    userId: item?.user_id || null,
    name: person.name,
    badge: person.badge === NA ? NA : person.badge,
    avatar: person.avatar,
    text: displayOrNa(item?.body),
    time: formatRelativeTime(item?.created_at),
    likes: Number.isFinite(likes) ? Math.max(0, likes) : 0,
    replies: Number.isFinite(replies) ? Math.max(0, replies) : 0,
    liked: Boolean(item?.has_current_user_liked),
    author: Boolean(
      authorUserId &&
      item?.user_id &&
      String(item.user_id) === String(authorUserId),
    ),
    attachment: null,
    raw: item,
  }
}

/**
 * Flatten paginated response + likes payloads into top-level threads
 * with nested reply comments (via parent_comment_id / parent).
 * Replies are never shown as extra roots.
 */
export const buildCallResponseThreads = ({
  responses = [],
  likes = [],
  authorUserId = null,
  callId = null,
} = {}) => {
  const flat = collectCallResponseItems(responses)
  const scoped = callId
    ? flat.filter(item => isCallResponseForCall(item, callId))
    : flat

  const usersById = {}

  const rememberUser = (user, userId) => {
    const id = user?.id || userId
    if (id && user) {
      usersById[String(id)] = user
    }
  }

  likes.forEach(like => rememberUser(like?.user, like?.user_id))
  scoped.forEach(item => {
    rememberUser(item?.user, item?.user_id)
    rememberUser(item?.parent?.user, item?.parent?.user_id)
    nestedResponseLists(item).forEach(list => {
      list.forEach(child => {
        rememberUser(child?.user, child?.user_id)
        rememberUser(child?.parent?.user, child?.parent?.user_id)
      })
    })
  })

  const byRawId = new Map()
  scoped.forEach(item => {
    if (item?.id) byRawId.set(String(item.id), item)
  })

  const mapped = Array.from(byRawId.values()).map(item =>
    mapCallResponseItem(item, { usersById, authorUserId }),
  )

  const byId = new Map(mapped.map(item => [String(item.id), item]))
  const roots = []

  mapped.forEach(item => {
    item.comments = []
  })

  mapped.forEach(item => {
    if (item.parentId && byId.has(String(item.parentId))) {
      byId.get(String(item.parentId)).comments.push(item)
      return
    }
    if (!item.parentId) {
      roots.push(item)
    }
  })

  roots.forEach(root => {
    root.replies = root.comments.length
    root.comments.sort((a, b) => {
      const aTime = dayjs(a.raw?.created_at).valueOf()
      const bTime = dayjs(b.raw?.created_at).valueOf()
      return aTime - bTime
    })
  })

  return roots
}
